import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { FutureBidRow, MatchedOffer, DealPartyE, MATCHED_OFFER_CANCELED, MATCHED_OFFER_PENDING, MATCHED_OFFER_SENT, Note, Cost } from '@tradecafe/types/core';
import { get, includes, isEmpty, omit, pick } from 'lodash-es';
import { MatchedOffersService } from 'src/pages/admin/auction/matched-offer-overlay/matched-offers.service';
import { InventoryDealsFormService } from 'src/components/matched-offers/forms/inventory-deals-form/inventory-deals-form.service';
import { SupplierOfferOverlayService } from 'src/pages/admin/auction/supplier-offer-overlay/supplier-offer-overlay.service';
import {
  isOfferExpired,
  OffersService,
} from 'src/services/data/offers.service';
import { FutureBidRow2 } from './future-bids-list.columns';
import { FutureBidsApiService } from 'src/pages/admin/auction/future-bids/future-bids.service';
import { ToasterService } from '../../shared/toaster/toaster.service';
import { CompanyCreditFormService } from 'src/pages/admin/settings/companies/companies/company-credit-form/company-credit-form.service';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  loadLocations,
  selectLocationEntities,
} from '../../app/store/locations';
import { waitNotEmpty } from '../../shared/utils/wait-not-empty';
import {loadPricingTerms, selectPricingTermEntities} from '../../app/store/pricing-terms'
import { MatchedOfferOverlayService } from 'src/pages/admin/auction/matched-offer-overlay/matched-offer-overlay.service'
import { AmplitudeService } from 'src/shared/amplitude/amplitude.service';
import { ACCOUNT_FIELDS, loadAccounts, selectAccountEntities } from 'src/app/store/accounts';
import { map, take } from 'rxjs/operators';
import { InlineEditorComponent } from 'src/components/inline-editor/inline-editor.component';
import { FinanceService } from 'src/pages/admin/auction/matched-offer-overlay/finance.service';
import { CreditPoolService } from 'src/services/data/credit-pool.service';
import { NegativeMarginReasonFormService } from 'src/pages/admin/trading/deal-form/negative-margin-reason/negative-margin-reason.service';
import { NotesService } from 'src/services/data/notes.service';
import { AuthApiService } from 'src/api/auth';
import { DealsService } from 'src/services/data/deals.service';
import { CreateOfferFormService } from 'src/pages/admin/trading/deal-form/create-offer-form/create-offer-form.service';
import { ProductPlusService } from 'src/services/data/product-plus.service';

@Component({
  selector: 'tc-future-bids-list',
  templateUrl: './future-bids-list.component.html',
  styleUrls: ['./future-bids-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FutureBidsListComponent implements OnInit {
  private readonly supplierOfferOverlay = inject(SupplierOfferOverlayService)
  private offersService = inject(OffersService)
  private readonly matchedOfferService = inject(MatchedOffersService)
  protected router = inject(Router)
  private readonly inventoryDealsForm = inject(InventoryDealsFormService)
  private readonly CompanyCreditForm = inject(CompanyCreditFormService)
  private readonly futureBidsService = inject(FutureBidsApiService)
  private toaster = inject(ToasterService)
  private readonly MatchedOfferOverlay = inject(MatchedOfferOverlayService)
  protected store = inject(Store)
  private readonly Amplitude = inject(AmplitudeService)
  private readonly Finance = inject(FinanceService)
  private readonly CreditPool = inject(CreditPoolService)
  private readonly negativeMarginReasonForm = inject(NegativeMarginReasonFormService)
  private readonly notesService = inject(NotesService)
  private readonly authApi = inject(AuthApiService)
  private readonly deals = inject(DealsService)
  private readonly createOfferForm = inject(CreateOfferFormService)
  private readonly productPlus = inject(ProductPlusService)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly destroyRef = inject(DestroyRef)

  protected getSign = (v) => v >= 0 ? '+' : '-';
  protected getAbsolutePrice = (v) => Math.abs(v);
  protected readonly STATUS_PENDING = MATCHED_OFFER_PENDING
  protected readonly STATUS_SENT = MATCHED_OFFER_SENT

  @Input() dataSource: MatTableDataSource<FutureBidRow2>;
  @Input() displayColumns: string[];
  @Input() columnNames: Dictionary<string>;
  @Input() showPaginator = true;
  @Input() showColumnSettings = true;

  @Output() editColumns = new EventEmitter<void>();
  @Output() listChange = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Input() sortState?: Sort;

  onMatSortChange(sort: Sort) {
    this.sorted = sort.active === 'futureDates' || sort.active === 'shipmentDates';
    this.sortChange.emit(sort);
  }

  @ViewChild(MatTable) table!: MatTable<FutureBidRow2>;

  @ViewChild(InlineEditorComponent)
  inlineEditor: InlineEditorComponent

  protected getDealUrl = (dealId: string) =>
    '/trading/deals' + '/' + dealId

  protected async showMatchedOfferDialog (matchedOffer: MatchedOffer) {
    this.MatchedOfferOverlay.showEditOffer(matchedOffer)
  }

  protected readonly locations$ = this.store.pipe(
    select(selectLocationEntities),
    waitNotEmpty(),
  );

  protected readonly pricingTerms$ = this.store.pipe(select(selectPricingTermEntities), waitNotEmpty());

  protected readonly accounts$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty());

  @ViewChild(MatPaginator) paginatorRef: MatPaginator;

  @ViewChild(MatSort) sortRef: MatSort;
  protected readonly isExpired = isOfferExpired;
  sorted: boolean;

  ngOnInit(): void {
    this.store.dispatch(loadAccounts({ fields: ACCOUNT_FIELDS.FUTURE_BIDS }));
    this.store.dispatch(loadPricingTerms({}));
    this.store.dispatch(loadLocations({}));
    // prefix/suffix catalogs for the "Product +" column
    this.productPlus.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
  }

  protected getProductPlus(row: FutureBidRow2): string {
    return this.productPlus.format(
      row.matchedOffer?.offer?.attributes?.extended_name,
      row.productName,
      row.product,
    );
  }

  async addOfferOverlay(row: FutureBidRow2) {
    const supplier_offer_id: string = row.supplier_offer_id;
    const offer = await this.offersService.getById(supplier_offer_id);
    const selectedOffer = {
      ...omit(offer, ['offer_id', 'status', 'buyers']),
      future_bid_id: get(row, 'future_bid_id'),
      future_bid_price: get(row, 'price'),
      buyers: [parseFloat(row.buyer)],
      ship_start: get(row, 'shipmentDates.from'),
      ship_end: get(row, 'shipmentDates.to'),
      bidOverwrites: {
        price: get(row, 'bidding.bidPrice'),
        ship_end: get(row, 'futureDates.to'),
        ship_start: get(row, 'futureDates.from'),
      },
    };
    this.supplierOfferOverlay
      .addOffer({
        ...selectedOffer,
        attributes: {
          ...selectedOffer.attributes,
          future_bid_id: get(row, 'future_bid_id'),
          future_bid_price: get(row, 'price'),
        },
        old_matched_offer_id: get(row, 'matched_offer_id'),
        old_supplier_offer_id: get(row, 'supplier_offer_id'),
        biddable: false,
      })
      .subscribe((offer) => {
        if (offer) {
          this.listChange.emit();
        }
      });
  }

  async showSupplierOffer(load_parent_supplier_offer_id: string) {
    const offer = await this.offersService.getById(
      load_parent_supplier_offer_id,
    );
    this.supplierOfferOverlay.editOffer(offer).subscribe();
  }

  async searchInventory(row: FutureBidRow2) {

    //todo: we should not to do this request, task created WA-19563
    const matchedOffer = await this.matchedOfferService.getById(row.matched_offer_id);
    const extendedName = {} as Record<string, string>;
    const suffix = this.productPlus.getSuffix(matchedOffer.offer.attributes.extended_name?.[matchedOffer.bid.product]?.suffix);
    if(suffix) {
      extendedName.suffix = suffix;
    }
    if (!matchedOffer) return;
    const formula = get(matchedOffer, 'bid.formula');
    const options = {
      ...row,
      matchedOffer: {
        isFormulaOffer: !!formula,
        formula,
        bid: matchedOffer.bid,
        shipmentDates: {
          from: matchedOffer.offer.ship_start,
          to: matchedOffer.offer.ship_end,
        },
        offer: {
          buyers: [row.buyer],
        },
      },
      extendedName,
    };
    this.inventoryDealsForm.show(options).subscribe((value) => {
      if (value) {
        this.listChange.emit();
      }
    });
  }

  async showMatchedOffer(supplier_offer_id: string) {
    this.router.navigateByUrl(
      `/trading/matched-offers?supplierOffersIds=${supplier_offer_id}`,
    );
  }

  protected hasPendingFutureBidMo(row: FutureBidRow2): boolean {
    if (!row?.isNotSubRow) return false;
    const matchedOfferId = row.matched_offer_id;
    if (!matchedOfferId) return false;
    return this.dataSource?.data?.some((r: any) =>
      r &&
      !r.isNotSubRow &&
      r.matched_offer_id === matchedOfferId &&
      r.matched_offers?.some((mo: any) => mo?.status === MATCHED_OFFER_PENDING),
    ) ?? false;
  }

  /**
   * Show "Assign to CO's Inv Deal" on a future bid when the buyer declined the current bid
   * (removed its delivery date) on a Current Offer that is associated with an Inventory Deal
   * (buyer is BWI inventory and the offer carries an origin/inventory deal).
   */
  protected canAssignToInvDeal(row: FutureBidRow2): boolean {
    if (!row?.is_future_bid) return false
    const mo = row.matchedOffer
    if (!mo) return false
    // A canceled current bid MO means its inventory deal was already released/consumed.
    if (get(mo, 'status') === MATCHED_OFFER_CANCELED) return false
    // The AO/SO must be associated with an Inventory Deal (buyer == Inventory).
    if (!get(mo, 'offer.bwi_inventory')) return false
    // The buyer must have declined the current (non-future) bid by removing its delivery date.
    return this.dataSource?.data?.some((r: FutureBidRow2) =>
      r &&
      r.matched_offer_id === row.matched_offer_id &&
      !r.is_future_bid &&
      r.is_delivery_date_active === false,
    ) ?? false
  }

  /**
   * Run the same process as Assign Future Bid, but against the Inventory Deal associated with
   * the Current Offer. Cancelling the SO linked to the current bid's MO frees that inventory
   * deal so it can be assigned to the future bid.
   */
  async assignToCurrentOfferInvDeal(row: FutureBidRow2) {
    const mo = row.matchedOffer
    const inventoryDealId = get(mo, 'offer.origin_deal_id') as string
    if (!mo || !inventoryDealId) return

    const eventDetails = {
      matched_offer_id: row.matched_offer_id,
      future_bid_id: row.future_bid_id,
      inventory_deal_id: inventoryDealId,
    }
    this.track('Assign to CO Inv Deal - Clicked', eventDetails)

    try {
      // Cancel the SO linked to the current bid's MO to free the inventory deal.
      const currentOffer = await this.offersService.getById(row.supplier_offer_id)
      if (currentOffer) {
        await this.offersService.cancelOffers([currentOffer])
      }

      // Same flow as Assign Future Bid, with the Current Offer's inventory deal.
      const dv = await this.deals
        .getDealView(inventoryDealId, ['deal', 'offers', 'costs', 'invoices', 'bids', 'files', 'segments'])
        .toPromise()

      const supplierOffer = await this.createOfferForm.showCreateOffer(dv, {
        defaultBuyers: row.buyer ? [String(row.buyer)] : [],
        costs: (mo.costs ?? []) as Cost[],
        disableBiddable: false,
        futureBidId: row.future_bid_id,
        futureBidPrice: Number(row.price),
        futureShipmentDates: row.shipmentDates,
        futureDeliveryDates: row.futureDates,
        futureDeliveryLocation: get(mo, 'bid.delivery'),
        assignFutureBid: true,
        minimalAssignMode: true,
        futureBidIsFormula: !!get(mo, 'bid.formula'),
      })

      if (!supplierOffer) {
        return
      }

      this.track('Assign to CO Inv Deal - Assigned', { ...eventDetails, supplier_offer_id: supplierOffer.offer_id })
      this.toaster.success('Inventory deal assigned to future bid')
      this.listChange.emit()
    } catch (e) {
      this.track('Assign to CO Inv Deal - Failed', eventDetails)
      this.toaster.error('Unable to assign inventory deal to future bid')
      console.error('assignToCurrentOfferInvDeal error', e)
    }
  }

  protected viewAllMatchedOffers(row: FutureBidRow2) {
    const matchedOfferId = row.matched_offer_id;
    if (!matchedOfferId) return;
    const ids = new Set<string>();
    ids.add(matchedOfferId);
    this.dataSource?.data?.forEach((r: any) => {
      if (!r || r.matched_offer_id !== matchedOfferId) return;
      r.matched_offers?.forEach((mo: any) => {
        if (mo?.matched_offer_id) ids.add(mo.matched_offer_id);
      });
    });
    this.router.navigateByUrl(
      `/trading/matched-offers?matchedOfferIds=${Array.from(ids).join(',')}`,
    );
  }

  async createDeal(futureBid: FutureBidRow) {
    this.futureBidsService.createDealFromFutureBid(futureBid);
  }

  track (eventName: string, eventDetails: Record<string, any>) {
    return this.Amplitude.track(`Future Bids: ${eventName}`, {
      ...eventDetails,
      page: 'Future Bids',
    });
  };

  async confirmBid(row: FutureBidRow2) {
    const activeBid = row.matchedOffer.bids.find(x => x.active);

    if (!activeBid) {
      this.toaster.error('Unable to confirm bid', 'No active bid found');
      return;
    }

    // Handle negative margin if present (single matched offer only)
    if (get(row, 'bidding.bidPartialMargin', 0) < 0) {
      await this.handleNegativeMarginMatchedOffer(row.matchedOffer);
    }

    const supplierId = row.matchedOffer.offer.account;
    const buyerId = row.matchedOffer.bid.account;
    const [supplier, buyer] = await Promise.all([
      this.accounts$.pipe(map(accounts => accounts[supplierId]), take(1)).toPromise(),
      this.accounts$.pipe(map(accounts => accounts[buyerId]), take(1)).toPromise(),
    ]);

    const eventDetails: Record<string, any> = {
      offer_id: row.matchedOffer.supplier_offer_id,
      matched_offer_id: row.matchedOffer.matched_offer_id,
      confirmation_type: 'bid',
      supplier_info: {
        account_id: supplier?.account || supplierId,
        name: supplier?.name,
      },
      buyer_info: {
        account_id: buyer?.account || buyerId,
        name: buyer?.name,
      },
      bid: pick(activeBid, ['bid_id', 'user_id', 'amount']),
    };

    this.track('Confirm Bid - Clicked', eventDetails);

    this.matchedOfferService
      .win(row.matched_offer_id)
      .then((res) => {
        if (res) {
          this.track('Confirm Bid - Confirmed', eventDetails);
          this.dataSource.data.forEach((i) => {
            if (i.matched_offer_id === row.matched_offer_id) {
              i.isExpired = true;
            }
          });
          this.table.renderRows();
          this.toaster.success('Bid is confirmed');
        } else {
          this.track('Confirm Bid - Failed', {
            ...eventDetails,
            error: 'unknown',
          });
          this.toaster.error('Unable to confirm bid');
        }
      })
      .catch((e) => {
        let toasterMessage = '';
        const errorMessage = e?.error?.error?.message;

        if (
          includes(
            errorMessage,
            'You are unable to make this purchase due to a credit issue',
          )
        ) {
          toasterMessage =
            'Buyer does not have sufficient credit to purchase product(s)';
        } else if (!isEmpty(errorMessage)) {
          toasterMessage = errorMessage;
        }

        this.track('Confirm Bid - Failed', {
          ...eventDetails,
          error: toasterMessage,
        });
        this.toaster.error('Unable to confirm bid', toasterMessage);
        console.log(e);
      });
  }

  showCompanyCredit(futureBid: FutureBidRow) {
    this.CompanyCreditForm.showCompanyCredit(futureBid.buyer);
  }

  protected async inlineMoPriceEdit(row: FutureBidRow2, cellEl: HTMLElement) {
    if (!row) return
    // allow edit only for primary rows (or when sorted to flatten), not expired
    if (!(row.isNotSubRow || this.sorted) || row.isExpired) return
    // allow only for ACTIVE/PENDING (SENT or PENDING) matched offers
    if (!(row.matchedOffer?.status === MATCHED_OFFER_PENDING || row.matchedOffer?.status === MATCHED_OFFER_SENT)) return
    try {
      const newPrice = await this.inlineEditor.editNumber(() => row['buyer_price'] || 0, cellEl, 0.0025)
      if (newPrice === row['buyer_price']) return

      // Load full matched offer by id (avoid using partial row data)
      const matchedOffer = await this.matchedOfferService.getById(row.matched_offer_id)
      if (!matchedOffer) return

      matchedOffer.bid.price = newPrice

      // Compute payment terms and term dates same as Matched Offers
      const delivery_dates = { from: matchedOffer.bid?.ship_start, to: matchedOffer.bid?.ship_end }
      const shipment_dates = { from: matchedOffer.offer?.ship_start, to: matchedOffer.offer?.ship_end }
      await this.getBalance(matchedOffer)
      this.calculateTermDate({ matchedOffer, delivery_dates, shipment_dates })

      await this.Finance.calculateMatchedOffer(matchedOffer)
      await this.matchedOfferService.calculateOffer(matchedOffer)
      await this.matchedOfferService.updateOffer(matchedOffer)

      // Reflect updated value in the table
      row['buyer_price'] = newPrice
      this.table.renderRows()
      this.toaster.success('MO Price updated')
      this.listChange.emit()
    } catch (e) {
      if (e !== 'canceled inline editor') {
        this.toaster.error('Unable to update MO Price')
        console.warn('inlineMoPriceEdit error', e)
      }
    }
  }

  /**
   * Handle negative margin for a single matched offer by creating a note.
   */
  private async handleNegativeMarginMatchedOffer(matchedOffer: MatchedOffer): Promise<void> {
    const {reason} = await this.negativeMarginReasonForm.show({
      matchedOffer,
      otherMatchedOfferIds: []
    });

    const currentUser = this.authApi.currentUser;
    const {user_id, account} = currentUser;

    const note = {
      body: reason.description,
      visibility: 1,
      attributes: {
        category: 'negative-margin',
        account,
        user_id,
      },
    };

    await this.notesService.createNote({
      ...note,
      matched_offer_id: matchedOffer.matched_offer_id
    } as Partial<Note>).catch((err) => {
      this.toaster.error(`Unable to create negative margin note for ${matchedOffer.matched_offer_id}`, err);
      throw err;
    });
  }

  // Payment terms helpers (mirror Matched Offers component)
  private supplierPaymentTerms: any
  private buyerPaymentTerms: any

  private async getBalance(matchedOffer: MatchedOffer): Promise<void> {
    const supplierId = matchedOffer.offer.account
    const buyerId = matchedOffer.bid.account

    const [supplierPaymentTerms, [, buyerPaymentTerms]] = await Promise.all([
      this.CreditPool.getPaymentTerms(supplierId, DealPartyE.supplier),
      this.CreditPool.getFor(buyerId).then(creditPool => {
        const buyerAccount = creditPool?.account ?? buyerId
        return this.CreditPool.getPaymentTerms(buyerAccount, DealPartyE.buyer)
          .then(payment_terms => [creditPool, payment_terms])
      }),
    ])

    this.supplierPaymentTerms = supplierPaymentTerms
    this.buyerPaymentTerms = buyerPaymentTerms
  }

  private calculateTermDate(params: {
    matchedOffer: MatchedOffer
    changedField?: string
    shipment_dates: { from?: number; to?: number }
    delivery_dates: { from?: number; to?: number }
    supplierPaymentTerms?: any
    buyerPaymentTerms?: any
  }): void {
    const { matchedOffer, changedField, shipment_dates, delivery_dates, supplierPaymentTerms = this.supplierPaymentTerms, buyerPaymentTerms = this.buyerPaymentTerms } = params

    this.store.pipe(select(selectAccountEntities), waitNotEmpty(), take(1)).subscribe(accounts => {
      const supplier = accounts[matchedOffer.offer.account]
      const buyer = accounts[matchedOffer.bid.account]

      if (!changedField || this.Finance.isFinanceDate(changedField, supplierPaymentTerms, true)) {
        const { termDate, balanceDate, liabilityDate, liabilityDateIsWeighted } = this.Finance.getTermDates({
          created: matchedOffer.created,
          shipmentDate: shipment_dates.to,
          deliveryDate: delivery_dates.to,
          paymentTerms: supplierPaymentTerms,
          account: supplier,
          supplier: true,
        })
        matchedOffer.attributes.supplier_term_date = termDate || undefined
        matchedOffer.attributes.supplier_balance_liability_date = balanceDate
        matchedOffer.attributes.supplier_liability_date = liabilityDate
        matchedOffer.attributes.supplier_anticipated_liability_is_weighted = liabilityDateIsWeighted
      }

      if (!changedField || this.Finance.isFinanceDate(changedField, buyerPaymentTerms)) {
        const { termDate, liabilityDate } = this.Finance.getTermDates({
          created: matchedOffer.created,
          shipmentDate: shipment_dates.to,
          deliveryDate: delivery_dates.to,
          paymentTerms: buyerPaymentTerms,
          account: buyer,
        })
        matchedOffer.attributes.buyer_term_date = termDate || undefined
        matchedOffer.attributes.collection_date = liabilityDate
      }
    })
  }
}
